import { get, intersection, isEmpty } from "lodash";
import { type ParsedQuery } from "query-string";
import { getCredits } from "@src/app/types/canonical/Credits";
import { matchesRegex } from "../../core/validation/validation";

export const FILTER_REGEX = /^[^,]+=[^,]+(,[^,]+)*$/;

export function filterValidator(name: string, example: string) {
  return matchesRegex(FILTER_REGEX, name, example);
}

export type ParsedFilterValue = string[];
export type ParsedFilter = {
  [key: string]: ParsedFilterValue;
};

export function parseFilter(filter: string | undefined): ParsedFilter {
  if (!filter) {
    return {};
  }

  return filter.split("&").reduce((acc, condition) => {
    const [key, value] = condition.split("=").map((v) => v.trim());
    if (!isEmpty(key)) {
      acc[key] = value.split(",").map((v) => v.trim());
    }
    return acc;
  }, {} as ParsedFilter);
}

export function getFirstFilter(filter: string | undefined): {
  key: string;
  values: ParsedFilterValue;
} {
  const parsed = parseFilter(filter);
  const key = Object.keys(parsed)[0];
  return {
    key: key || "",
    values: parsed[key] || []
  };
}

export type ObjectProperties = {
  [key: string]: string | string[] | undefined;
};

export function getProperties(obj: unknown): ObjectProperties {
  return flattenObject(obj);
}

function flattenObject(input: unknown, parentKey = ""): ObjectProperties {
  if (typeof input !== "object" || input === null) {
    return {};
  }

  const result: ObjectProperties = {};
  const entries = Object.entries(input as Record<string, unknown>);

  for (const [key, value] of entries) {
    const fullKey = parentKey ? `${parentKey}.${key}` : key;

    if (value === null || value === undefined) {
      continue;
    }

    if (Array.isArray(value)) {
      result[fullKey] = value.map(String);
    } else if (typeof value === "object") {
      Object.assign(result, flattenObject(value, fullKey));
    } else {
      result[fullKey] = String(value);
    }
  }

  result.credits = getCredits(get(input, "credits"))?.toString();
  result.language = get(input, "language.externalId");
  result.year = get(input, "year.id");

  return result;
}

export function matchesFilter(
  filter: string | undefined,
  properties: ObjectProperties
): boolean {
  if (!filter) {
    return true;
  }

  const parsedFilter = parseFilter(filter);

  for (const [key, values] of Object.entries(parsedFilter)) {
    const value = properties[key];
    if (value instanceof Array && intersection(value, values).length > 0) {
      return true;
    } else if (value && values.includes(value as string)) {
      return true;
    }
  }

  return false;
}

export function getFilterParam(
  name: string,
  queryParams: ParsedQuery<string>
): string[] {
  const param = queryParams[name];
  if (param) {
    if (!Array.isArray(param)) {
      return [param];
    }
    return param as string[];
  }
  return [];
}
